import {Alert, Button, Card, Col, message, Modal, Space, Table, Tag, Tooltip} from "antd";
import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import {DownloadOutlined, EyeOutlined, PlusOutlined} from "@ant-design/icons";
import {
    COMPONENT_TYPE,
    DOC_CODE_INVEST_TRANSACTION_DOCUMENT,
    NOT_AVAILABLE,
    PREVIEW_DOC_TYPES, YYYY_MM_DD
} from "../../../util/Constants";
import {AccountWiseFundDetailDto} from "../../../dto/AccountWiseFundDetailDto";
import transactionFetchService from "../../../service/transactionFetchService";
import {
    MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR,
    MSG_FAILED_TO_FETCH_PORTFOLIO_LIST_ERROR,
    MSG_UNABLE_TO_FETCH_INVEST_DOCUMENTS_ERROR
} from "../../../util/Messages";
import ErrorHandler from "../../../util/ErrorHandler";
import AddNewInvestModal from "../../../pages/admin/investReconciliation/modals/addNewInvestModal";
import PreviewModalComp from "../../common/preview/PreviewModalComp";
import documentService from "../../../service/documentService";
import {toNumber} from "lodash";
import {UploadedDocumentDetailDto} from "../../../dto/UploadedDocumentDetailDto";
import TableSearchComp from '../../common/tableSearch/TableSearchComp';
import {TableColumnsType} from 'antd/lib/table';
import CommonFunction from "../../../util/CommonFunction";
import {faEnvelope} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";

import type { DraggableData, DraggableEvent } from 'react-draggable';
import Draggable from 'react-draggable';
import {ReconciliationTableDataDto} from "../../../dto/admin/ReconciliationTableDataDto";
interface CustomerInvestRequestTableCompProps {
    spinnerLoad: (spin: boolean) => void
    dataSource?: AccountWiseFundDetailDto[]
    compType: COMPONENT_TYPE
    isCanShowRowSelection: boolean
    cardSize: string
    isCanShowAlert?: boolean
    isCanShowButton?: boolean
    bankRecord?: ReconciliationTableDataDto[]
}

const CustomerInvestRequestTableComp = forwardRef((props: CustomerInvestRequestTableCompProps, ref) => {

    const [customerRequestList, setCustomerRequestList] = useState<AccountWiseFundDetailDto[]>([]);
    const [updateCustomerRequest, setUpdateCustomerRequest] = useState<boolean>(false);
    const [isNewInvestModalOpen, setIsNewInvestModalOpen] = useState(false);
    const [isTransReceiptModalOpen, setIsTransReceiptModalOpen] = useState(false);
    const [docURL, setDocURL] = useState('');
    const [documentName, setDocumentName] = useState<string>("Transaction Receipt");
    const [selectedRecordIdList, setSelectedRecordIdList] = useState<number[]>();
    const [docType, setDocType] = useState<PREVIEW_DOC_TYPES>();
    const [bounds, setBounds] = useState({ left: 0, top: 0, bottom: 0, right: 0 });
    const draggleRef = useRef<HTMLDivElement>(null);
    const [disabled, setDisabled] = useState(true);
    const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
        const { clientWidth, clientHeight } = window.document.documentElement;
        const targetRect = draggleRef.current?.getBoundingClientRect();
        if (!targetRect) {
            return;
        }
        setBounds({
            left: -targetRect.left + uiData.x,
            right: clientWidth - (targetRect.right - uiData.x),
            top: -targetRect.top + uiData.y,
            bottom: clientHeight - (targetRect.bottom - uiData.y),
        });
    };

    useEffect(() => {
        let compType = COMPONENT_TYPE.ACTION == props.compType;
        if (compType) {
            fetchCustomerRequestList()
        } else {
            setCustomerRequestList(props.dataSource as AccountWiseFundDetailDto[])
        }
    }, [updateCustomerRequest]);

    useImperativeHandle(ref, () => ({

        getSelectedIdList(): number[] {
            return selectedRecordIdList as number[];
        }

    }));
    const fetchCustomerRequestList = () => {
        props.spinnerLoad(true)
        transactionFetchService.getPendingInvestCustomerRequests().then(res => {
            if (res.data !== null) {
                if (props.isCanShowRowSelection) {
                    let mappedResults = res.data.results.map((record: any, index: any) => ({
                        ...record,
                        key: index.toString(),
                    }));
                    setCustomerRequestList(mappedResults)
                    props.spinnerLoad(false)
                } else {
                    setCustomerRequestList(res.data.results)
                    props.spinnerLoad(false)
                }
            } else {
                message.error(MSG_FAILED_TO_FETCH_PORTFOLIO_LIST_ERROR).then();
                props.spinnerLoad(false)
            }

        }, (reason) => {
            ErrorHandler.handleError(reason);
            props.spinnerLoad(false)
        })
    }

    const customerRequestsColumns: TableColumnsType<AccountWiseFundDetailDto> = [
        {
            title: 'CIN',
            dataIndex: 'customerCin',
            sorter: (a: any, b: any) => {
                a = a.customerCin == null ? '' : a.customerCin
                b = b.customerCin == null ? '' : b.customerCin
                return a.localeCompare(b);
            },
            ...TableSearchComp('customerCin', 'CIN'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            sorter: (a: any, b: any) => {
                a = a.customerName == null ? '' : a.customerName
                b = b.customerName == null ? '' : b.customerName
                return a.localeCompare(b);
            },
            ...TableSearchComp('customerName', 'Customer Name'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'NIC No.',
            dataIndex: 'customerNic',
            sorter: (a: any, b: any) => {
                a = a.customerNic == null ? '' : a.customerNic
                b = b.customerNic == null ? '' : b.customerNic
                return a.localeCompare(b);
            },
            ...TableSearchComp('customerNic', 'NIC No.'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Portfolio ID',
            dataIndex: 'portfolioNumber',
            sorter: (a: any, b: any) => {
                a = a.portfolioNumber == null ? '' : a.portfolioNumber
                b = b.portfolioNumber == null ? '' : b.portfolioNumber
                return a.localeCompare(b);
            },
            ...TableSearchComp('portfolioNumber', 'Portfolio ID'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Fund',
            dataIndex: 'fundName',
            sorter: (a: any, b: any) => {
                a = a.fundName == null ? '' : a.fundName
                b = b.fundName == null ? '' : b.fundName
                return a.localeCompare(b);
            },
            ...TableSearchComp('fundName', 'Fund'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Amount',
            dataIndex: 'fundAmount',
            align: 'right',
            sorter: (a: any, b: any) => a.fundAmount - b.fundAmount,
            ...TableSearchComp('fundAmount', 'Amount'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{toFixed(text)}</span>)
            }

        },
        {
            title: 'Collection Bank',
            dataIndex: 'collectionBank',
            sorter: (a: any, b: any) => {
                a = a.collectionBank == null ? '' : a.collectionBank
                b = b.collectionBank == null ? '' : b.collectionBank
                return a.localeCompare(b);
            },
            ...TableSearchComp('collectionBank', 'Collection Bank'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Ref. No.',
            dataIndex: 'transactionRefNo',
            sorter: (a: any, b: any) => {
                a = a.transactionRefNo == null ? '' : a.transactionRefNo
                b = b.transactionRefNo == null ? '' : b.transactionRefNo
                return a.localeCompare(b);
            },
            ...TableSearchComp('transactionRefNo', 'Ref. No.'),
            render: (text: any, rec: any, index: number) => {
                return (<span>{text ? text : NOT_AVAILABLE}</span>)
            }
        },
        {
            title: 'Started Date',
            dataIndex: 'startedDate',
            key: 'startedDate',
            sorter: (a: any, b: any) => {
                a = a.startedDate == null ? '' : a.startedDate
                b = b.startedDate == null ? '' : b.startedDate
                return a.localeCompare(b);
            },
            ...TableSearchComp('startedDate', 'Started Date'),
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )

        },
        {
            title: 'Process Date',
            dataIndex: 'processDate',
            key: 'processDate',
            sorter: (a: any, b: any) => {
                a = a.processDate == null ? '' : a.processDate
                b = b.processDate == null ? '' : b.processDate
                return a.localeCompare(b);
            },
            ...TableSearchComp('processDate', 'Process Date'),
            render: (text: any) => (
                <span>{null != text ? dayjs(text).format(YYYY_MM_DD) : NOT_AVAILABLE}</span>
            )
        },
        {
            title: 'Reject Reason',
            dataIndex: 'reconciliationRejectReason',
            align: 'center',
            fixed: 'right' as const,
            render: (text: any, rec: any, index: number) => {

                return (<span>{text ?
                    <Tooltip placement="leftTop"
                             title={text}>
                        <Tag color={'red'} key={text}>REJECTED <FontAwesomeIcon icon={faEnvelope}/> </Tag>
                    </Tooltip>
                    : <Tag color={'default'} key={text}>{NOT_AVAILABLE}</Tag>}</span>)
            }
        },
        {
            title: 'Trans. Receipt',
            dataIndex: 'tr_receipt',
            key: 'tr_receipt',
            align: 'center',
            fixed: 'right' as const,
            render: (text: any, rec: AccountWiseFundDetailDto) => (
                <Space size="middle">
                    <Button className='btn-action-outline-common btn-action-view '
                            icon={<EyeOutlined/>} onClick={() => showTransReceiptModal(rec)}>View</Button>
                </Space>
            ),
        },
    ];

    const showNewInvestModal = () => {
        setIsNewInvestModalOpen(true);
    };

    const showTransReceiptModal = (record: AccountWiseFundDetailDto) => {
        props.spinnerLoad(true)
        documentService.getUploadDocumentListByTypeAndUploadedDocId(toNumber(record.jobNumber), record.transactionDocumentId as number, [DOC_CODE_INVEST_TRANSACTION_DOCUMENT]).then(res => {
            if (res.data.status == 'SUCCESS') {
                viewOriginalDocument(res.data.results[0].uploadDocumentDetailDto);
            } else {
                message.error(MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR).then();
                props.spinnerLoad(false)
            }
        }, (reason) => {
            message.warning(MSG_UNABLE_TO_FETCH_INVEST_DOCUMENTS_ERROR).then();
            props.spinnerLoad(false)
        });
    }

    const viewOriginalDocument = (document: UploadedDocumentDetailDto) => {
        props.spinnerLoad(true)
        documentService.viewOriginalDocument(document.uploadDocumentDetailId.toString()).then(res => {
            if (res.data !== null) {
                setIsTransReceiptModalOpen(true);
                setDocURL(res.data.results)
                setDocumentName(document.originalDocumentName)
                setDocType(CommonFunction.checkDocumentContentType(res.data.results))
                props.spinnerLoad(false)
            } else {
                props.spinnerLoad(false)
                message.error(MSG_FAILED_TO_FETCH_DOCUMENTS_ERROR).then();

            }
        }, (reason) => {
            ErrorHandler.handleError(reason);
            props.spinnerLoad(false)
        })
    };

    const toFixed = (value: number | undefined): any => {
        if (undefined != value) {
            let val = value.toString();
            if (val.indexOf(".") != -1) {
                val = val.slice(0, (val.indexOf(".")) + 3);
            }
            val = (+val).toFixed(2);
            return val.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        }
        return "0.00";
    }

    const download = (val: string, docName: string) => {
        let a = document.createElement('a');
        a.href = val;
        a.download = docName;
        a.click();
    }

    const handleNewInvestModalCancel = () => {
        props.spinnerLoad(true)
        setIsNewInvestModalOpen(false);
    };
    const onUpdate = () => {
        setUpdateCustomerRequest(!updateCustomerRequest);
        props.spinnerLoad(false)
    };

    const handleTransReceiptModalCancel = () => {
        setIsTransReceiptModalOpen(false);
    };

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: AccountWiseFundDetailDto[]) => {
            let idList: number[] = [];
            selectedRows.forEach((row) => {
                idList.push(row.accountWiseFundId)
            });
            setSelectedRecordIdList(idList)
        },
    };
    return (
        <>
            <Col span={24}>
                <Card title={<div className={'customer-requests-card-custom-title'}>
                    <span className="card-title">Customer Requests</span>
                    {props.isCanShowAlert ?
                        <span className="card-info"><Alert
                            message="Please select the customer records that should be rejected in this process"
                            type="info"/> </span> : <></>}
                </div>}
                      size={props.cardSize}
                      extra={props.isCanShowButton ? (
                          <Button type="primary" className={'add-new-invest-btn'} icon={<PlusOutlined/>}
                                  onClick={showNewInvestModal}>Add New
                              Invest</Button>) : <></>}>

                    <Table className={'recon-customer-requests-table'} scroll={{x: 'max-content'}}
                           columns={customerRequestsColumns} dataSource={customerRequestList}
                           rowSelection={props.isCanShowRowSelection ? {...rowSelection} : undefined}/>
                </Card>
            </Col>
            <Modal
                   title={
                       <div
                           style={{ width: '100%', cursor: 'move', paddingTop:20, fontSize: 16 }}
                           onMouseOver={() => {
                               if (disabled) {
                                   setDisabled(false);
                               }
                           }}
                           onMouseOut={() => {
                               setDisabled(true);
                           }}
                           onFocus={() => {}}
                           onBlur={() => {}}
                       >
                           Add New Invest
                       </div>
                   }
                   open={isNewInvestModalOpen}
                   destroyOnClose={true}
                   maskClosable={false}
                   footer={null}
                   onCancel={() => {
                       setIsNewInvestModalOpen(false)
                   }}
                   centered
                   className='recon-new-invest-modal'
                   modalRender={(modal: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined) => (
                       <Draggable
                           disabled={disabled}
                           bounds={bounds}
                           nodeRef={draggleRef}
                           onStart={(event, uiData) => onStart(event, uiData)}
                       >
                           <div ref={draggleRef}>{modal}</div>
                       </Draggable>
                   )}
            >
                <AddNewInvestModal bankRecord={props.bankRecord} onClose={handleNewInvestModalCancel} onUpdate={onUpdate}/>
            </Modal>

            <Modal title={
                       <div
                           style={{ width: '100%', cursor: 'move', fontSize: 16 }}
                           onMouseOver={() => {
                               if (disabled) {
                                   setDisabled(false);
                               }
                           }}
                           onMouseOut={() => {
                               setDisabled(true);
                           }}
                           onFocus={() => {}}
                           onBlur={() => {}}
                       >
                           {documentName}
                       </div>
                   }
                   open={isTransReceiptModalOpen}
                   destroyOnClose={true}
                   maskClosable={false}
                   onCancel={handleTransReceiptModalCancel}
                   centered
                // className='dashboard-account-open-modal'
                   className={docType == PREVIEW_DOC_TYPES.PDF ? "modal-custom-bg" : "image-view-modal"}
                   width={docType == PREVIEW_DOC_TYPES.PDF ? 700 : 500}
                   footer={[
                       <Button type="primary" icon={<DownloadOutlined/>} style={{background: '#FF7400', height: 40}}
                               htmlType="submit" onClick={() => download(docURL, documentName)}>
                           Download
                       </Button>]}
                   modalRender={(modal: string | number | boolean | React.ReactElement<any, string | React.JSXElementConstructor<any>> | Iterable<React.ReactNode> | React.ReactPortal | null | undefined) => (
                       <Draggable
                           disabled={disabled}
                           bounds={bounds}
                           nodeRef={draggleRef}
                           onStart={(event, uiData) => onStart(event, uiData)}
                       >
                           <div ref={draggleRef}>{modal}</div>
                       </Draggable>
                   )}
            >


                <PreviewModalComp docURL={docURL}/>
            </Modal>
        </>
    )
});
export default CustomerInvestRequestTableComp;
